import { useState } from 'react';
import { useMutation, useQuery } from 'convex/react';
import { api } from '../../../../convex/_generated/api';

const STATE_LABEL: Record<string, string> = {
  overdue: 'calibration overdue',
  due_soon: 'calibration due soon',
  unknown: 'no calibration recorded',
  ok: '',
};

export default function Team() {
  const members = useQuery(api.members.list, {});
  const testers = useQuery(api.testers.list, {});
  const billing = useQuery(api.billing.current, {});
  const setActive = useMutation(api.members.setActive);
  const setRole = useMutation(api.members.setRole);
  const createTester = useMutation(api.testers.create);

  const [brand, setBrand] = useState('');
  const [model, setModel] = useState('');
  const [serial, setSerial] = useState('');
  const [due, setDue] = useState('');
  const [error, setError] = useState<string | null>(null);

  return (
    <>
      <h1>Team &amp; instruments</h1>

      <h2>Technicians</h2>
      <div className="card">
        {billing ? (
          <p className="muted small">
            {billing.seatsUsed} of {billing.seats} seat{billing.seats === 1 ? '' : 's'} in use
            {billing.seatsExceeded ? ' — over your plan' : ''}.
          </p>
        ) : null}
        <table>
          <thead><tr><th>Name</th><th>Role</th><th>Competency</th><th>Active</th><th /></tr></thead>
          <tbody>
            {(members ?? []).map((m) => (
              <tr key={m._id}>
                <td>{m.displayName}</td>
                <td className="muted">{m.role}</td>
                <td className="muted small">{m.competencyRef ?? '—'}</td>
                <td>{m.active ? 'yes' : <span className="pill">no</span>}</td>
                <td>
                  <div className="row">
                    <button
                      className="quiet"
                      onClick={async () => {
                        setError(null);
                        try { await setActive({ memberId: m._id, active: !m.active }); }
                        catch (e) { setError(e instanceof Error ? e.message : 'Could not change'); }
                      }}
                    >
                      {m.active ? 'Deactivate' : 'Activate'}
                    </button>
                    <button
                      className="quiet"
                      onClick={async () => {
                        setError(null);
                        try {
                          await setRole({
                            memberId: m._id,
                            role: m.role === 'owner' ? 'technician' : 'owner',
                          });
                        } catch (e) { setError(e instanceof Error ? e.message : 'Could not change'); }
                      }}
                    >
                      Make {m.role === 'owner' ? 'technician' : 'owner'}
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {error ? <p className="small" style={{ color: 'var(--alert)' }}>{error}</p> : null}
        <p className="muted small">
          Technicians are invited through your organisation in the account menu. They appear
          here once they accept.
        </p>
      </div>

      <h2>Test instruments</h2>
      <div className="card">
        <p className="muted small">
          Every test record cites the instrument's serial and calibration date, because that is
          what an auditor asks for.
        </p>
        <table>
          <thead><tr><th>Instrument</th><th>Serial</th><th>Calibration due</th><th /></tr></thead>
          <tbody>
            {(testers ?? []).filter((t) => t.active).map((t) => (
              <tr key={t._id}>
                <td>{t.brand} {t.model}</td>
                <td className="tag">{t.serial}</td>
                <td className={t.calibrationState === 'overdue' ? 'overdue' : ''}>
                  {t.calibrationDueAt
                    ? new Date(t.calibrationDueAt).toLocaleDateString('en-AU')
                    : '—'}
                </td>
                <td className="muted small">{STATE_LABEL[t.calibrationState]}</td>
              </tr>
            ))}
            {testers?.filter((t) => t.active).length === 0 ? (
              <tr><td colSpan={4} className="muted">No instruments registered.</td></tr>
            ) : null}
          </tbody>
        </table>

        <label>Add an instrument</label>
        <div className="row">
          <input value={brand} onChange={(e) => setBrand(e.target.value)} placeholder="Seaward" />
          <input value={model} onChange={(e) => setModel(e.target.value)} placeholder="Apollo 600" />
          <input value={serial} onChange={(e) => setSerial(e.target.value)} placeholder="Serial" />
          <input type="date" value={due} onChange={(e) => setDue(e.target.value)} />
          <button
            disabled={!brand || !model || !serial}
            onClick={async () => {
              setError(null);
              try {
                await createTester({
                  brand, model, serial,
                  ...(due ? { calibrationDueAt: new Date(due).getTime() } : {}),
                });
                setBrand(''); setModel(''); setSerial(''); setDue('');
              } catch (e) { setError(e instanceof Error ? e.message : 'Could not add'); }
            }}
          >
            Add
          </button>
        </div>
      </div>
    </>
  );
}
